import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CreditCard, Shield, Calendar, Home, AlertCircle, Users } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Rules = () => {
  const [content, setContent] = useState({});

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/content`);
      const contentMap = {};
      response.data.forEach(item => {
        contentMap[item.section_key] = item.content;
      });
      setContent(contentMap);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  // Parse rules from content (stored as JSON array string)
  const parseRules = (contentKey, defaultRules) => {
    try {
      if (content[contentKey]) {
        const parsed = JSON.parse(content[contentKey]);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {
      // If not JSON, try splitting by newline or return as single item
      if (content[contentKey]) {
        const text = content[contentKey];
        if (text.includes('\n')) return text.split('\n').filter(r => r.trim());
        return [text];
      }
    }
    return defaultRules;
  };

  const sections = [
    {
      icon: CreditCard,
      title: 'Rent & Payment Policies',
      color: 'purple',
      rules: parseRules('rules_rent_payment', [
        'Rent is due on the 1st of every month',
        'Accepted payment methods: Bank Transfer, UPI, Cheque',
        'Late payment after 5th of the month will incur a penalty of ₹500',
        'Rent includes room charges and basic amenities',
        'Electricity and water charges are included in monthly rent',
        'Payment receipt will be provided for all transactions',
        'Annual rent increase will not exceed 10%'
      ])
    },
    {
      icon: Shield,
      title: 'Security Deposit Rules',
      color: 'orange',
      rules: parseRules('rules_deposit', [
        'Security deposit is equal to 2 months rent',
        'Deposit must be paid before move-in',
        'Fully refundable upon checkout (subject to property inspection)',
        'Deposit will be refunded within 15 days of checkout',
        'Deductions may apply for damages or outstanding dues',
        'Deposit does not count as advance rent',
        'Deposit will be held in a secure account'
      ])
    },
    {
      icon: Calendar,
      title: 'Notice Period & Lock-in',
      color: 'purple',
      rules: parseRules('rules_notice_period', [
        'Minimum lock-in period: 3 months',
        'Notice period for vacating: 30 days',
        'Written notice required via email or letter',
        'Early termination within lock-in: 1 month rent as penalty',
        'Notice period can be adjusted for job transfers (with proof)',
        'Refund processing after successful checkout',
        'Transfer/subletting not allowed without written permission'
      ])
    },
    {
      icon: Home,
      title: 'House Rules',
      color: 'orange',
      rules: parseRules('rules_house_rules', [
        'Entry timing: Open 24x7 with biometric access',
        'Visitor policy: Allowed in common areas until 10 PM',
        'Overnight guests require prior approval',
        'Smoking and alcohol consumption strictly prohibited inside premises',
        'Loud music/noise prohibited after 10 PM',
        'Keep rooms and common areas clean',
        'Pets not allowed',
        'Use of illegal substances will result in immediate eviction',
        'Maintenance issues should be reported to management immediately'
      ])
    },
    {
      icon: Users,
      title: 'Community Guidelines',
      color: 'purple',
      rules: parseRules('rules_community', [
        'Respect fellow residents privacy and space',
        'Common area facilities to be shared equally',
        'Kitchen usage: Clean utensils after use',
        'Laundry: Use designated days and timings',
        'Parking: Use only allotted spaces',
        'Waste disposal: Use designated bins and segregate waste',
        'Report suspicious activities to management',
        'Be courteous and maintain a peaceful environment'
      ])
    },
    {
      icon: AlertCircle,
      title: 'Violations & Penalties',
      color: 'orange',
      rules: parseRules('rules_violations', [
        'First violation: Written warning',
        'Second violation: Fine of ₹1,000',
        'Third violation: Termination of agreement',
        'Serious violations: Immediate eviction without refund',
        'Damage to property: Repair cost + penalty',
        'Illegal activities: Police complaint + immediate eviction',
        'Management decision on violations is final',
        'Appeals can be made in writing within 48 hours'
      ])
    }
  ];

  return (
    <div className="pt-16">
      <section className="bg-gradient-to-br from-white via-purple-50/30 to-white py-20 md:py-32">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-6">
              Rules & <span className="text-brand-purple">Regulations</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-gray-600 font-body max-w-3xl mx-auto">
              Please read and understand our policies to ensure a harmonious living experience
            </p>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-6" data-testid="rules-accordion">
            {sections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <AccordionItem 
                  value={`item-${index}`} 
                  className="bg-white border border-gray-100 rounded-2xl px-6 shadow-sm hover:shadow-md transition-all"
                  data-testid={`rules-section-${index}`}
                >
                  <AccordionTrigger className="hover:no-underline py-6">
                    <div className="flex items-center gap-4 text-left">
                      <div className={`w-12 h-12 bg-brand-${section.color}/10 rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <section.icon className={`text-brand-${section.color === 'purple' ? 'purple' : 'orange'}`} size={24} />
                      </div>
                      <h2 className="font-heading text-xl md:text-2xl font-semibold text-gray-900">
                        {section.title}
                      </h2>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2 pb-6">
                    <ul className="space-y-3 ml-16">
                      {section.rules.map((rule, ruleIndex) => (
                        <li key={ruleIndex} className="flex items-start gap-3" data-testid={`rule-${index}-${ruleIndex}`}>
                          <div className="w-2 h-2 bg-brand-purple rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700 font-body leading-relaxed">{rule}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 bg-brand-purple/5 border border-brand-purple/20 rounded-2xl p-8 text-center"
            data-testid="rules-disclaimer"
          >
            <AlertCircle className="text-brand-purple mx-auto mb-4" size={32} />
            <p className="text-gray-700 font-body leading-relaxed">
              Management reserves the right to modify rules and regulations as needed. 
              Residents will be notified of any changes with 15 days notice. For queries, 
              please contact the management office.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
